import { DivProps } from '@commonTypes'
import React, { FC, useEffect, useState } from 'react'
import cx from 'classnames'
import { sortBy } from 'lodash'
import { Menu, MenuItem, Tab, Tabs, Position, MenuDivider } from '@blueprintjs/core'
import Appbar from 'components/Appbar'
import api, { IBuilding, IPortfolioBuilding, ICurrentUser, IPortfolioFloor } from '@api'
import { useUser, useKeynav } from '@hooks'
import {
  PageContent,
  HeroCards,
  Icon,
  IconButton,
  LocationSelect,
  ModalLayer,
  HiddenLocationIndicator,
  IGridTableColumn,
  NewGridTable,
  PrettyNumber,
  Popover,
} from '@ui'
import { Mode } from '@ui/selects/LocationSelect/types'
import Details from './Tabs/index'
import { Router, NavigateFn } from '@reach/router'
import queryString from 'query-string'
import { Nav } from '@utils'
import track from '@track'
import { useAppData } from '@providers'
import css from './portfolioDetails.module.scss'

interface ITabContext {
  object: IPortfolioBuilding | IPortfolioFloor | null
  user: ICurrentUser | null
  units?: 'sqm' | 'sqft'
  loading: boolean
}

export const TabContext = React.createContext<ITabContext>({
  object: null,
  user: null,
  loading: false,
})

export enum PortfolioDetailTabs {
  sbd = 'sbd',
  sebd = 'sebd',
  abd = 'abd',
  st = 'st',
  sbut = 'sbut',
}

interface IProps {
  buildingId: string
  floorId?: string
  navigate: NavigateFn
}

function Number({ value }: { value: string | number }) {
  return <PrettyNumber value={value} />
}

export default function PortfolioDetails({ buildingId, floorId, navigate }: IProps) {
  const [building, setBuilding] = useState<IBuilding | null>(null)
  const { user } = useUser()
  const units = user?.context?.units
  const keynav = useKeynav(Object.values(PortfolioDetailTabs))
  const [loading, setLoading] = useState(false)

  let floor

  const search = queryString.parse(window.location.search)

  useEffect(() => {
    if (
      search.tab &&
      keynav.val !== search.tab &&
      Object.values(PortfolioDetailTabs).includes(search.tab as PortfolioDetailTabs)
    ) {
      keynav.setVal(search.tab as string)
    }

    track.portfolio({
      eventName: track.Event.NAVIGATE,
      props: {
        pageName: `Portfolio ${floorId ? 'Details: Building' : 'Details: Floor'}`,
      },
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  if (floorId) floor = building?.portfolio?.floors?.find((f) => f.floor.id === floorId)

  const portfolio = floor || building?.portfolio

  const loadBuilding = () => {
    setLoading(true)
    api.portfolio
      .building({ id: buildingId, assignmentPlanId: search.apId as string })
      .then((data: any) => setBuilding(data.node))
      .finally(() => setLoading(false))
  }
  useEffect(loadBuilding, [buildingId])

  const cards = [
    {
      value: portfolio?.seatedCount,
      label: 'Seated Employees',
      fontSize: 26,
    },
    {
      value: portfolio?.workstations,
      label: 'Total Seats',
      fontSize: 26,
    },
    {
      value: portfolio?.unoccupied,
      label: 'Available Seats',
      fontSize: 26,
    },
  ]
  if (portfolio && 'floors' in portfolio)
    cards.push({
      value: portfolio?.floors?.length,
      label: 'Floors',
      fontSize: 26,
    })

  return (
    <>
      <Appbar
        title={
          <div className="f f-align-center nowrap">
            <span style={{ marginRight: 8 }}>
              <IconButton
                tooltip="Back to portfolio"
                minimal
                iconName="chevron-left"
                onClick={() => Nav.portfolio({})}
              />
            </span>
            <span>Portfolio details for /</span>
            <LocationSelect
              buttonProps={{ minimal: true }}
              mode={Mode.SINGLE_BUILDING_FLOOR}
              value={floorId || buildingId}
              onChange={(location) => {
                if ('building' in location)
                  return Nav.portfolio({
                    buildingId: location.building.id,
                    floorId: location.id,
                  })
                Nav.portfolio({ buildingId: location.id })
              }}
            />
          </div>
        }
      />
      <PageContent>
        <PrintTitle>
          <h1>{building?.name}</h1>
          {Boolean(floor) && <h4>{floor?.floor?.name}</h4>}
        </PrintTitle>
        <div className="f mv4">
          <HeroCards cards={cards} />
          <Popover
            content={<MoreMenu navigate={navigate} building={building} floor={floor} />}
            position={Position.BOTTOM}
            minimal
          >
            <IconButton tooltip="More actions" iconName="ellipsis-h" round={false} />
          </Popover>
        </div>
        <div className="portfolio">
          <TabContext.Provider value={{ object: portfolio || null, user, units, loading }}>
            <Tabs selectedTabId={keynav.val} onChange={keynav.setVal}>
              <Tab id="sbd" title="Seats By Department" panel={<Details.SeatsByDepartment />} />
              <Tab
                id="sebd"
                title="Seated Employees By Department"
                panel={<Details.SeatedEmployeesByDepartment />}
              />
              <Tab id="abd" title="Area By Department" panel={<Details.AreaByDepartment />} />
              <Tab id="st" title="Space Types" panel={<Details.SpaceTypes />} />
              <Tab id="sbut" title="Spaces By Usage Types" panel={<Details.SpacesByUsageTypes />} />
            </Tabs>
          </TabContext.Provider>
        </div>
        {!floorId && (
          <div className="mt5">
            <FloorTable
              building={building}
              units={units}
              floorId={floorId}
              buildingId={buildingId}
              navigate={navigate}
            />
          </div>
        )}
      </PageContent>
      <Router primary={false}>
        <ModalLayer reload={loadBuilding} path="*" />
      </Router>
    </>
  )
}

interface IFloorTableProps {
  building: IBuilding | null
  buildingId: string
  floorId?: string
  units?: 'sqft' | 'sqm'
  navigate: (to: string) => void
}

function FloorTable({ floorId, building, buildingId, units }: IFloorTableProps) {
  const portfolio = building?.portfolio
  if (!portfolio) return null

  const columns: IGridTableColumn<IPortfolioFloor & { id: string }>[] = [
    {
      label: 'Floor Name',
      Cell: ({ cell }) => {
        if (!cell) return null

        const name = cell.data.floor.name

        return floorId !== cell.data.floor.id ? (
          <span
            className={css.link}
            data-test="floor-name"
            title={`Go to ${name} Details`}
            onClick={() => Nav.portfolio({ buildingId, floorId: cell.data.floor.id })}
          >
            {name}
            {cell.data.floor.hidden && (
              <span className="ml2">
                <HiddenLocationIndicator floor />
              </span>
            )}
          </span>
        ) : (
          <div>{cell.data.floor.name}</div>
        )
      },
    },
    {
      label: 'Total Seats',
      alignRight: true,
      tooltip: (
        <p style={{ maxWidth: 300 }}>
          The physical seat count on the floor. Includes physical seats of all types. Excludes
          neighborhood capacity.
        </p>
      ),
      Cell: ({ cell }) => {
        if (!cell) return null

        return (
          <span data-test="total-seats">
            <Number value={cell.data.allocated} />
          </span>
        )
      },
    },
    {
      label: 'Available Seats',
      alignRight: true,
      tooltip: (
        <p style={{ maxWidth: 300 }}>
          The number of seats of all types that do not have any assigned employee. Total seats minus
          occupied seats. Because of neighborhoods and secondary seats, this can be different from
          the number of employees at this location.
        </p>
      ),
      Cell: ({ cell }) => {
        if (!cell) return null

        return (
          <span data-test="available-seats">
            <Number value={cell.data.unoccupied} />
          </span>
        )
      },
    },
    {
      label: 'Occupied Seats',
      alignRight: true,
      tooltip: (
        <p style={{ maxWidth: 300 }}>
          The total number of seats with assigned employees or occupied status. This includes
          primary, secondary, reserved, neighborhood, hoteling, and hot seats.
        </p>
      ),
      Cell: ({ cell }) => {
        if (!cell) return null

        return (
          <span data-test="occupied-seats">
            <Number value={cell.data.occupied} />
          </span>
        )
      },
    },
    {
      label: 'Total Employees',
      alignRight: true,
      tooltip: (
        <p style={{ maxWidth: 300 }}>
          Number of employees that are seated on the floor. Includes primary seats and
          neighborhoods. Excludes unseated, remote/no desk needed employees.
        </p>
      ),
      Cell: ({ cell }) => {
        if (!cell) return null

        return (
          <span data-test="total-employees">
            <Number value={cell.data.seatedCount} />
          </span>
        )
      },
    },
    {
      label: 'Density',
      alignRight: true,
      tooltip: (
        <p style={{ maxWidth: 300 }}>
          Area per seat in square {units}. Calculated by dividing the total square{' '}
          {units === 'sqm' ? 'meters' : 'feet'} by the total number of seats.
        </p>
      ),
      Cell: ({ cell }) => {
        if (!cell) return null

        return (
          <span data-test="density">
            <Number value={units === 'sqft' ? cell.data.densitySqft : cell.data.densitySqrm} />
          </span>
        )
      },
    },
    {
      label: units === 'sqft' ? 'Sqft' : 'Sqm',
      tooltip: (
        <p style={{ maxWidth: 300 }}>
          The area of the floor in square {units === 'sqm' ? 'meters' : 'feet'} taken from the
          building/floor information.
        </p>
      ),
      alignRight: true,
      Cell: ({ cell }) => {
        if (!cell) return null

        return (
          <span data-test="units">
            <Number value={units === 'sqft' ? cell.data.densitySqft : cell.data.densitySqrm} />
          </span>
        )
      },
    },
    {
      label: 'Occupancy',
      tooltip: (
        <p style={{ maxWidth: 300 }}>
          The percentage of total seats that are occupied. Total seats minus unoccupied seats,
          divided by total seats. Excludes neighborhood capacity.
        </p>
      ),
      Cell: ({ cell }) => {
        if (!cell) return null

        return (
          <div data-test="occupancy" className="w-full">
            <NewGridTable.PercentBar value={cell.data.occupancy} />
          </div>
        )
      },
    },
  ]

  const formatFloors = (items: IPortfolioFloor[]) => {
    return items.map((b) => ({
      data: { id: b.floor.id, ...b },
    }))
  }

  const formatedFloors = formatFloors(sortBy(portfolio.floors, 'floor.story') as IPortfolioFloor[])

  return (
    <NewGridTable<IPortfolioFloor & { id: string }>
      status={'idle'}
      columns={columns}
      data={formatedFloors}
    />
  )
}

export const PrintTitle: FC<React.PropsWithChildren<DivProps>> = ({ className, ...props }) => (
  <div className={cx(css.print_title, className)} {...props} />
)

interface IMoreMenuProps {
  navigate: (to: string) => void
  building: IBuilding | null
  floor?: IPortfolioFloor
}

function MoreMenu({ navigate, building, floor }: IMoreMenuProps) {
  const { product } = useAppData()
  const stackingEnabled = product.features.includes('stacking')

  if (!building) return null

  return (
    <Menu>
      <MenuItem
        text="Print / Save as PDF"
        icon={<Icon style={{ marginTop: 2 }} name="print" />}
        onClick={window.print}
      />
      {stackingEnabled && (
        <MenuItem
          text="Go to stack"
          icon={<Icon style={{ marginTop: 2 }} name="layer-plus" />}
          onClick={() => {
            Nav.stack({
              city: building.city,
              buildingId: building.id,
              floorId: floor?.floor?.id,
            })
          }}
        />
      )}
      {/* {!floor && (
        <MenuItem
          text="Go to projections"
          icon={<Icon style={{ marginTop: 2 }} name="chart-line" />}
          onClick={() => Nav.projections({})}
        />
      )} */}
      <MenuDivider />
      <MenuItem
        text={`Manage ${floor ? 'floor' : 'building'}`}
        icon={<Icon style={{ marginTop: 2 }} name="pen" />}
        onClick={() => {
          if (floor) return Nav.quickFloor({ floorId: floor.floor.id })
          Nav.quickBuilding({ buildingId: building.id })
        }}
      />
    </Menu>
  )
}
